'use client';

import { useMemo, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { API_URL, NewsItem } from '@/lib/api';
import SiteHeader from '@/components/SiteHeader';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import NewsImage from '@/components/NewsImage';
import NewsFilters from '@/components/NewsFilters';
import NewsPagination from '@/components/NewsPagination';
import { NewsSkeleton, NewsError, NewsEmpty } from '@/components/NewsLoadingStates';
import { IconCalendar, IconEye, IconGrid, IconList, IconLoader, IconMessageCircle, IconThumbUp, IconUser, IconBookOpen } from '@/components/ui/MonoIcons';
import { getNewsDisplayMeta } from '@/utils/newsBlocksMeta';
import { getCoverFocusFromAtmosphere, focusToObjectPosition, focusToZoom } from '@/utils/coverFocus';
import { useNews } from '@/lib/swr';
import useSWR from 'swr';
import { fetcher } from '@/lib/swr/fetcher';

interface Category {
  id: number;
  name: string;
  slug: string;
  color: string;
  description?: string;
}

interface Tag {
  id: number;
  name: string;
  slug: string;
}

interface NewsPageData {
  results: NewsItem[];
  count: number;
  next?: string;
  previous?: string;
}

export default function NewsPage() {
  const router = useRouter();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const itemsPerPage = 12;

  // SWR хук для новостей с пагинацией и фильтрами
  const { news, totalCount, isLoading: loading, error: newsError, mutate } = useNews({
    page: currentPage,
    limit: itemsPerPage,
    category: selectedCategory || undefined,
    tag: selectedTag || undefined,
    search: searchQuery.trim() || undefined,
    ordering: '-published_at',
  });

  // SWR для категорий
  const { data: categoriesData } = useSWR<Category[]>(
    '/api/news/categories/',
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );
  const categories = Array.isArray(categoriesData) ? categoriesData : [];

  // SWR для тегов
  const { data: tagsData } = useSWR<Tag[]>(
    '/api/news/tags/',
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );
  const tags = Array.isArray(tagsData) ? tagsData : [];

  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
  const error = newsError ? (newsError instanceof Error ? newsError.message : 'Ошибка загрузки') : null;

  const navigateToNews = (slug: string) => {
    router.push(`/news/${encodeURIComponent(String(slug || '').trim())}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAuthorName = (author: NewsItem['author']) => {
    if (!author) return 'Неизвестный автор';
    if (author.first_name || author.last_name) {
      return `${author.first_name || ''} ${author.last_name || ''}`.trim();
    }
    return String((author as any).username || '').trim() || 'Автор';
  };

  const resetFilters = () => {
    setSelectedCategory('');
    setSelectedTag('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleTagChange = (tag: string) => {
    setSelectedTag(tag);
    setCurrentPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSelectedCategory('');
    setSelectedTag('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  if (loading && news.length === 0) {
  return (
    <div className="min-h-screen text-site-text relative overflow-hidden">

        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <IconLoader className="w-12 h-12 animate-spin mx-auto mb-4 text-site-secondary" />
            <p className="text-xl text-site-secondary">Загружаем новости...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-site-text relative overflow-hidden">

      
      {/* Навигация */}
      <SiteHeader />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pb-12">
        {/* Заголовок */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-site-text mb-4">Новости лаборатории</h1>
          <p className="text-xl text-site-secondary">Последние исследования и достижения в области ИИ</p>
        </div>

        {/* Фильтры и поиск */}
        <NewsFilters
          categories={categories}
          tags={tags}
          selectedCategory={selectedCategory}
          selectedTag={selectedTag}
          searchQuery={searchQuery}
          onCategoryChange={handleCategoryChange}
          onTagChange={handleTagChange}
          onSearchChange={handleSearchChange}
          onClearFilters={handleClearFilters}
          className="mb-8"
        />

        {/* Переключатель вида и статистика */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-4 items-center">
            <span className="text-site-secondary">
              Найдено: <span className="text-site-accent font-semibold">{totalCount}</span> новостей
            </span>
          </div>

          {/* Переключатель вида */}
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'list' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="flex items-center gap-2"
              icon={<IconList />}
            >
              Список
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="flex items-center gap-2"
              icon={<IconGrid />}
            >
              Сетка
            </Button>
          </div>
        </div>

        {/* Список новостей */}
        {error ? (
          <NewsError error={error} onRetry={() => mutate()} />
        ) : loading ? (
          <NewsSkeleton count={itemsPerPage} variant={viewMode === 'grid' ? 'default' : 'compact'} />
        ) : news.length === 0 ? (
          <NewsEmpty message="Новости не найдены" />
        ) : (
          <>
            <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 xl:grid-cols-3 gap-6' : 'space-y-6'}>
              {news.map((item) => (
                (() => {
                  const display = getNewsDisplayMeta(item as any);
                  // Настройки ракурса обложки
                  const cf = getCoverFocusFromAtmosphere((item as any)?.page_atmosphere);
                  const objPos = focusToObjectPosition(cf);
                  const cfZoom = focusToZoom(cf);
                  return (
                <Card
                  key={item.id}
                  variant="glass"
                  useWidgetSettings={true}
                  className="overflow-hidden hover:scale-[1.02] transition-all duration-300 cursor-pointer"
                  onClick={() => navigateToNews(item.slug)}
                >
                  {viewMode === 'grid' ? (
                    // Grid View
                    <div className="h-full flex flex-col">
                      {/* Изображение */}
                      <div className="relative h-48 overflow-hidden">
                        <NewsImage
                          src={item.cover_url}
                          alt={display.title}
                          fill
                          className="rounded-t-lg"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          objectPosition={objPos}
                          zoom={cfZoom}
                          priority={false}
                        />
                        
                        {/* Категория поверх изображения */}
                        {item.category && (
                          <div className="absolute top-3 left-3">
                            <Badge 
                              variant="custom" 
                              color={item.category.color}
                              size="sm"
                            >
                              {item.category.name}
                            </Badge>
                          </div>
                        )}
                      </div>

                      {/* Контент */}
                      <div className="p-6 flex-1 flex flex-col">
                        <h3 className="text-xl font-bold text-site-text mb-3 line-clamp-2 hover:text-site-accent transition-colors">
                          {display.title}
                        </h3>

                        <p className="text-site-secondary mb-4 flex-1 line-clamp-3">
                          {display.teaser}
                        </p>

                        {/* Теги */}
                        {item.tags && item.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {item.tags.slice(0, 3).map((tag) => (
                              <Badge key={tag.slug} variant="secondary" size="sm">
                                {tag.name}
                              </Badge>
                            ))}
                            {item.tags.length > 3 && (
                              <Badge variant="secondary" size="sm">
                                +{item.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* Мета-информация */}
                        <div className="border-t border-site-border pt-3">
                          {/* Автор и статистика в одной строке */}
                          <div className="flex items-center justify-between">
                            {/* Левая часть - автор и дата */}
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-site-action rounded-full flex items-center justify-center text-sm font-bold text-site-on-accent">
                                {(() => {
                                  const n = getAuthorName(item.author);
                                  return (n.trim()[0] || '?').toUpperCase();
                                })()}
                              </div>
                              <div className="flex flex-col">
                                <span className="text-sm text-site-secondary">
                                  {getAuthorName(item.author)}
                                </span>
                                <span className="text-xs text-site-muted flex flex-wrap items-center gap-2">
                                  <span className="inline-flex items-center gap-1">
                                    <IconCalendar className="w-3.5 h-3.5" />
                                    <span>{formatDate(item.published_at)}</span>
                                  </span>
                                  {item.reading_time && (
                                    <span className="inline-flex items-center gap-1">
                                      <IconBookOpen className="w-3.5 h-3.5" />
                                      <span>{item.reading_time} мин</span>
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                            
                            {/* Правая часть - статистика */}
                            <div className="flex items-center gap-4 text-sm text-site-muted">
                              <span className="flex items-center gap-1">
                                <IconEye className="w-4 h-4" /> {item.views_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <IconMessageCircle className="w-4 h-4" /> {item.comments_count || 0}
                              </span>
                              <span className="flex items-center gap-1">
                                <IconThumbUp className="w-4 h-4" /> {item.likes_count || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // List View
                    <div className="flex gap-6 p-6">
                      {/* Изображение */}
                      <div className="relative w-48 h-32 flex-shrink-0 overflow-hidden rounded-lg">
                        <NewsImage
                          src={item.cover_url}
                          alt={display.title}
                          width={192}
                          height={128}
                          className="rounded-lg"
                          sizes="192px"
                          objectPosition={objPos}
                          zoom={cfZoom}
                          priority={false}
                        />
                      </div>

                      {/* Контент */}
                      <div className="flex-1 min-w-0">
                        {/* Категория и теги */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {item.category && (
                            <Badge 
                              variant="custom" 
                              color={item.category.color}
                              size="sm"
                            >
                              {item.category.name}
                            </Badge>
                          )}
                          {item.tags?.slice(0, 2).map((tag) => (
                            <Badge key={tag.slug} variant="secondary" size="sm">
                              {tag.name}
                            </Badge>
                          ))}
                        </div>

                        <h3 className="text-2xl font-bold text-site-text mb-3 hover:text-site-accent transition-colors">
                          {display.title}
                        </h3>

                        <p className="text-site-secondary mb-4 line-clamp-2">
                          {display.teaser}
                        </p>

                        {/* Мета-информация */}
                        <div className="flex items-center justify-between text-sm text-site-muted">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <IconUser className="w-4 h-4" /> {getAuthorName(item.author)}
                            </span>
                            <span className="flex items-center gap-1">
                              <IconCalendar className="w-4 h-4" /> {formatDate(item.published_at)}
                            </span>
                            {item.reading_time && (
                              <span className="flex items-center gap-1">
                                <IconBookOpen className="w-4 h-4" /> {item.reading_time} мин
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <IconEye className="w-4 h-4" /> {item.views_count}
                            </span>
                            <span className="flex items-center gap-1">
                              <IconMessageCircle className="w-4 h-4" /> {item.comments_count || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <IconThumbUp className="w-4 h-4" /> {item.likes_count || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
                  );
                })()
              ))}
            </div>

            {/* Пагинация */}
            {totalPages > 1 && (
              <div className="mt-8">
                <NewsPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}